import {
  Entity,
  Member,
  OwnershipRecord,
  Loan,
  NonMemberSigner,
  ChildMember,
} from '../types/ownership'

export const CURRENT_ENTITY: Entity = {
  id: 'ent-001',
  name: 'Acme Holdings LLC',
  recordType: 'Entity',
  businessIncorporateState: 'CA',
  ein: '82-4591023',
  foreignEntityRegistration: 'TX',
}

export const AVAILABLE_MEMBERS: Member[] = [
  {
    id: 'mem-001',
    name: 'John Smith',
    recordType: 'Individual',
    email: 'john.smith@example.com',
  },
  {
    id: 'mem-002',
    name: 'Sarah Johnson',
    recordType: 'Individual',
    email: 'sarah.j@example.com',
  },
  {
    id: 'mem-003',
    name: 'Michael Chen',
    recordType: 'Individual',
    email: 'm.chen@example.com',
  },
  {
    id: 'mem-004',
    name: 'Emily Davis',
    recordType: 'Individual',
    email: 'emily.d@example.com',
  },
  {
    id: 'mem-005',
    name: 'David Wilson',
    recordType: 'Individual',
    email: 'dwilson@example.com',
  },
]

// Helper to get date string relative to today
const getDate = (daysOffset: number) => {
  const date = new Date()
  date.setDate(date.getDate() + daysOffset)
  return date.toISOString().split('T')[0]
}

export const INITIAL_OWNERSHIP_RECORDS: OwnershipRecord[] = [
  {
    id: 'own-001',
    memberId: 'mem-001',
    memberName: 'John Smith',
    memberRecordType: 'Individual',
    entityId: 'ent-001',
    ownershipPercentage: 40,
    effectiveStartDate: getDate(-365),
    isPrimaryMember: true,
    createdDate: getDate(-365),
  },
  {
    id: 'own-002',
    memberId: 'mem-002',
    memberName: 'Sarah Johnson',
    memberRecordType: 'Individual',
    entityId: 'ent-001',
    ownershipPercentage: 30,
    effectiveStartDate: getDate(-180),
    isPrimaryMember: false,
    createdDate: getDate(-180),
  },
  {
    id: 'own-003',
    memberId: 'mem-003',
    memberName: 'Michael Chen',
    memberRecordType: 'Individual',
    entityId: 'ent-001',
    ownershipPercentage: 10,
    effectiveStartDate: getDate(-90),
    effectiveEndDate: getDate(-1),
    isPrimaryMember: false,
    createdDate: getDate(-90),
  },
  {
    id: 'own-004',
    memberId: 'mem-003',
    memberName: 'Michael Chen',
    memberRecordType: 'Individual',
    entityId: 'ent-001',
    ownershipPercentage: 15,
    effectiveStartDate: getDate(0),
    isPrimaryMember: false,
    createdDate: getDate(0),
  },
  {
    id: 'own-005',
    memberId: 'mem-ent-apex',
    memberName: 'Apex Ventures LLC',
    memberRecordType: 'Entity',
    memberEin: '55-1234567',
    entityId: 'ent-001',
    ownershipPercentage: 15,
    effectiveStartDate: getDate(-270),
    isPrimaryMember: false,
    createdDate: getDate(-270),
  },
]

// Children of entity members, keyed by memberId
export const ENTITY_MEMBER_CHILDREN: Record<string, ChildMember[]> = {
  'mem-ent-apex': [
    {
      id: 'cm-apex-001',
      name: 'Diana Reyes',
      recordType: 'Individual',
      ownershipPercentage: 60,
      ein: '77-1122334',
      email: 'd.reyes@apexventures.com',
      memberSince: getDate(-300),
      title: 'Managing Partner',
    },
    {
      id: 'cm-apex-002',
      name: 'Franklin Osei',
      recordType: 'Individual',
      ownershipPercentage: 25,
      ein: '77-5566778',
      email: 'f.osei@apexventures.com',
      memberSince: getDate(-300),
      title: 'Partner',
    },
    {
      id: 'cm-apex-003',
      name: 'Summit Capital Group',
      recordType: 'Entity',
      ownershipPercentage: 15,
      ein: '88-9900112',
      memberSince: getDate(-200),
      title: 'Investor',
    },
  ],
}

export const OPEN_LOANS: Loan[] = [
  {
    id: 'loan-001',
    loanNumber: 'LN-2024-001',
    borrowerName: 'Acme Holdings LLC',
    loanAmount: 500000,
    status: 'Active',
    entityId: 'ent-001',
    createdDate: getDate(-120),
  },
  {
    id: 'loan-002',
    loanNumber: 'LN-2024-042',
    borrowerName: 'Acme Holdings LLC',
    loanAmount: 1250000,
    status: 'Active',
    entityId: 'ent-001',
    createdDate: getDate(-60),
  },
  {
    id: 'loan-003',
    loanNumber: 'LN-2024-089',
    borrowerName: 'Acme Holdings LLC',
    loanAmount: 75000,
    status: 'Pending',
    entityId: 'ent-001',
    createdDate: getDate(-10),
  },
  {
    id: 'loan-004',
    loanNumber: 'LN-2023-992',
    borrowerName: 'Acme Holdings LLC',
    loanAmount: 300000,
    status: 'Active',
    entityId: 'ent-001',
    createdDate: getDate(-400),
  },
]

export const NON_MEMBER_SIGNERS: NonMemberSigner[] = [
  {
    id: 'nms-001',
    name: 'Robert Garcia',
    email: 'r.garcia@example.com',
    role: 'Guarantor',
    loanIds: ['loan-001', 'loan-002'],
    loanNumbers: ['LN-2024-001', 'LN-2024-042'],
    dateAdded: getDate(-120),
  },
  {
    id: 'nms-002',
    name: 'Linda Park',
    email: 'lpark@example.com',
    role: 'Guarantor',
    loanIds: ['loan-003'],
    loanNumbers: ['LN-2024-089'],
    dateAdded: getDate(-10),
  },
  {
    id: 'nms-003',
    name: 'Thomas Wright',
    role: 'Guarantor',
    loanIds: ['loan-004'],
    loanNumbers: ['LN-2023-992'],
    dateAdded: getDate(-400),
  },
]

export const CHILD_MEMBERS: ChildMember[] = [
  {
    id: 'cm-001',
    name: 'John Smith',
    recordType: 'Individual',
    ownershipPercentage: 40,
    ein: '12-3456789',
    email: 'john.smith@example.com',
    memberSince: getDate(-365),
    title: 'Managing Member',
  },
  {
    id: 'cm-002',
    name: 'Sarah Johnson',
    recordType: 'Individual',
    ownershipPercentage: 30,
    ein: '98-7654321',
    email: 'sarah.j@example.com',
    memberSince: getDate(-180),
    title: 'Member',
  },
  {
    id: 'cm-003',
    name: 'Apex Ventures LLC',
    recordType: 'Entity',
    ownershipPercentage: 20,
    ein: '55-1234567',
    memberSince: getDate(-270),
    title: 'Investor Entity',
  },
  {
    id: 'cm-004',
    name: 'Michael Chen',
    recordType: 'Individual',
    ownershipPercentage: 10,
    ein: '44-9876543',
    email: 'm.chen@example.com',
    memberSince: getDate(-90),
    title: 'Member',
  },
]

// Configuration table: state code → Judicial classification
// Judicial = foreclosure requires court proceedings
// Non-Judicial = power of sale, no court required
export const STATE_JUDICIAL_CONFIG: Record<
  string,
  'Judicial' | 'Non-Judicial'
> = {
  // Non-Judicial states
  AK: 'Non-Judicial',
  AL: 'Non-Judicial',
  AR: 'Non-Judicial',
  AZ: 'Non-Judicial',
  CA: 'Non-Judicial',
  CO: 'Non-Judicial',
  DC: 'Non-Judicial',
  GA: 'Non-Judicial',
  ID: 'Non-Judicial',
  MI: 'Non-Judicial',
  MN: 'Non-Judicial',
  MS: 'Non-Judicial',
  MO: 'Non-Judicial',
  MT: 'Non-Judicial',
  NV: 'Non-Judicial',
  NH: 'Non-Judicial',
  NC: 'Non-Judicial',
  OR: 'Non-Judicial',
  RI: 'Non-Judicial',
  TN: 'Non-Judicial',
  TX: 'Non-Judicial',
  UT: 'Non-Judicial',
  VA: 'Non-Judicial',
  WA: 'Non-Judicial',
  WV: 'Non-Judicial',
  WY: 'Non-Judicial',
  // Judicial states
  CT: 'Judicial',
  DE: 'Judicial',
  FL: 'Judicial',
  HI: 'Judicial',
  IL: 'Judicial',
  IN: 'Judicial',
  IA: 'Judicial',
  KS: 'Judicial',
  KY: 'Judicial',
  LA: 'Judicial',
  ME: 'Judicial',
  MD: 'Judicial',
  MA: 'Judicial',
  NE: 'Judicial',
  NJ: 'Judicial',
  NM: 'Judicial',
  NY: 'Judicial',
  ND: 'Judicial',
  OH: 'Judicial',
  OK: 'Judicial',
  PA: 'Judicial',
  SC: 'Judicial',
  SD: 'Judicial',
  VT: 'Judicial',
  WI: 'Judicial',
}
